const x = point => Array.isArray(point) ? point[0] : point && point.x;
const y = point => {
  const value = Array.isArray(point) ? point[1] : point && point.y;
  return typeof value === "string" && value.trim() ? Number(value) : value;
};
const scalar = point => point && Number.isFinite(x(point)) &&
  (y(point) === null || Number.isFinite(y(point)));

export function visiblePoints(data, range) {
  if (!range || !data.length) return data;
  // Include a neighbour on either side to keep lines continuous at zoom edges.
  let low = 0, high = data.length;
  while (low < high) { const mid = (low + high) >>> 1; if (x(data[mid]) < range.min) low = mid + 1; else high = mid; }
  const first = Math.max(0, low - 1);
  high = data.length;
  while (low < high) { const mid = (low + high) >>> 1; if (x(data[mid]) <= range.max) low = mid + 1; else high = mid; }
  return data.slice(first, Math.min(data.length, low + 1));
}

function sampleAligned(series, budget, stepIndices) {
  const length = series[0].data.length;
  if (length <= budget) return series;
  const retained = new Set([0, length - 1]);
  // One bucket can contribute a minimum and maximum from every series. All
  // aligned series use the same indices, preserving shared-tooltip timestamps.
  const buckets = Math.max(1, Math.floor((budget - 2) / (2 * series.length)));
  const bucketSize = Math.ceil((length - 2) / buckets);
  for (let start = 1; start < length - 1; start += bucketSize) {
    const end = Math.min(length - 1, start + bucketSize);
    series.forEach((entry, seriesIndex) => {
      let min = start, max = start;
      for (let i = start; i < end; i += 1) {
        const value = y(entry.data[i]), previous = y(entry.data[i - 1]);
        if (value !== null) {
          if (y(entry.data[min]) === null || value < y(entry.data[min])) min = i;
          if (y(entry.data[max]) === null || value > y(entry.data[max])) max = i;
        }
        // Gaps and switching edges are never averaged away. Very frequent
        // switching can intentionally exceed the display budget.
        if ((value === null) !== (previous === null) ||
            (stepIndices.has(seriesIndex) && value !== previous)) {
          retained.add(i - 1); retained.add(i);
        }
      }
      retained.add(min); retained.add(max);
    });
  }
  // The final point may itself be a switching/gap edge.
  series.forEach((entry, index) => {
    if (y(entry.data[length - 1]) !== y(entry.data[length - 2]) &&
        (stepIndices.has(index) || y(entry.data[length - 1]) === null || y(entry.data[length - 2]) === null)) {
      retained.add(length - 2);
    }
  });
  const indices = Array.from(retained).sort((a, b) => a - b);
  return series.map(entry => ({ ...entry, data: indices.map(i => entry.data[i]) }));
}

export function sampleChartSeries(series, budget = 1500, range = null, curves = []) {
  if (!Array.isArray(series) || !series.length || series.some(entry =>
    !entry || !Array.isArray(entry.data) || entry.data.some((point, i) =>
      !scalar(point) || (i > 0 && x(point) < x(entry.data[i - 1]))))) return series;
  const entries = series.map(entry => ({ ...entry, data: visiblePoints(entry.data, range) }));
  const aligned = entries.every(entry => entry.data.length === entries[0].data.length &&
    entry.data.every((point, i) => x(point) === x(entries[0].data[i])));
  const steps = new Set(entries.map((entry, i) =>
    (Array.isArray(curves) ? curves[i] : curves) === "stepline" ? i : -1));
  if (aligned && entries[0].data.length) return sampleAligned(entries, budget, steps);
  return entries.map((entry, index) => entry.data.length ?
    sampleAligned([entry], budget, new Set(steps.has(index) ? [0] : []))[0] : entry);
}

export function chartDomain(series) {
  const nonempty = (series || []).filter(entry => Array.isArray(entry.data) && entry.data.length);
  if (!nonempty.length) return null;
  const min = Math.min(...nonempty.map(entry => x(entry.data[0])));
  const max = Math.max(...nonempty.map(entry => x(entry.data[entry.data.length - 1])));
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}
